import type { AddendumChange } from '@/lib/schemas/db'

/**
 * The customer fields a revision tracks, with the labels shown in the change
 * history — the customer twin of `addendumFields.ts`.
 *
 * Deliberately excluded: `publicId` (immutable identity, never edited) and the
 * system timestamps. Everything staff can actually change is here, so no edit
 * can slip through untracked.
 */
export const CUSTOMER_REVISION_FIELDS = [
  { field: 'fullName', label: 'Full name' },
  { field: 'externalId', label: 'Omni ID' },
  { field: 'contactAddress', label: 'Address' },
  { field: 'contactEmail', label: 'Email' },
  { field: 'contactPhone', label: 'Phone' },
  { field: 'invoiceName', label: 'Invoice to' },
  { field: 'invoiceEmail', label: 'Invoice email' },
  { field: 'invoicePhone', label: 'Invoice phone' },
  { field: 'invoiceAddress', label: 'Invoice address' },
  { field: 'invoiceVat', label: 'VAT number' },
  { field: 'externalCustomerId', label: 'Secondary Omni ID' },
] as const

export type CustomerRevisionFieldKey =
  (typeof CUSTOMER_REVISION_FIELDS)[number]['field']

export function customerRevisionFieldLabel(field: string): string {
  return (
    CUSTOMER_REVISION_FIELDS.find((entry) => entry.field === field)?.label ??
    field
  )
}

/**
 * Diff a partial patch against the customer's current values, dropping
 * everything that did not actually change.
 *
 * Unlike `buildAddendumChanges` the second argument is a *patch*, not a full
 * draft: a field the patch does not mention is untouched, never "cleared".
 */
export function buildCustomerRevisionChanges(
  current: Record<string, string | undefined>,
  patch: Record<string, string | undefined>,
): AddendumChange[] {
  return CUSTOMER_REVISION_FIELDS.flatMap((entry) => {
    if (!(entry.field in patch) || patch[entry.field] === undefined) return []
    const before = current[entry.field] ?? ''
    const after = patch[entry.field] ?? ''
    if (before === after) return []
    return [{ field: entry.field, label: entry.label, before, after }]
  })
}
